// Fuente unica de "este libro todavia esta en preventa, y en que idiomas".
//
// Un libro no sale de una vez en los siete idiomas, asi que "esta en preventa"
// no es un booleano: es una pregunta por idioma. Escalas Ilustradas (61-67) es
// el precedente, entregado en dos tandas (el español el 15-ago-2026 y los otros
// seis el 1-sep-2026), y por eso ya no figura en ninguno de los tres mapas.
//
// Antes esto era un `preorder: true` suelto y repetido en once lugares
// (_collection/constants, home-libro2, home-libro2-catalogo, LibroContent,
// libro/[slug]/page, pricing, pay2, home-upsell, home-compra, home-universo y
// las landings de coleccion). Como el flag no sabia de idiomas, el 15-ago la
// web seguia diciendo "Preventa · Disponible el 15 de agosto" en español, un
// dia despues de haber entregado el libro. Por eso vive aca y no en cada
// pantalla: cualquier lugar que quiera decir "preventa" tiene que preguntar
// por idioma.
//
// AL LIBERAR UN LIBRO se borra SOLO SU CLAVE de los tres mapas de abajo. NUNCA
// se vacia un objeto: aca vive mas de un libro y dejar {} apaga la preventa de
// los otros. Ademas hay que borrar de los siete locales las claves de preventa
// de ESE libro ({slug}_preorder_badge, _preorder_dates, _buy_preorder,
// _videos_preorder_description, y en checkout el _subtext y el _presale) y
// borrar su bloque de PREORDER_NOTES en lib/books/meta.js. La
// lista de pantallas a mirar despues esta en el checklist de release de libro.
//
// Ojo: estos mapas NO son el unico gate. El boton de descarga del portal y las
// descargas rapidas de home-compra tienen sus propias listas de ids escritas a
// mano, y el PDF tiene que estar en R2 y mapeado en admin-back config/products.

// Idiomas en los que cada libro sigue SIN entregarse. Lista vacia / slug
// ausente = el libro salio en los siete idiomas.
export const PREORDER_LANGS_BY_SLUG = {
  // Musica Ilustrada de Cero (productos 71-77) sale entero en preventa: no se
  // entrego en ningun idioma todavia. tr (78) va aca mientras el turco este oculto
  // (lib/idiomas.js): al abrirlo, decidir su fecha en PREORDER_RELEASE_ISO_BY_SLUG.
  mic: ["es", "en", "pt", "fr", "it", "de", "ja", "tr"],
};

// Mismo dato indexado por producto base, para las pantallas que razonan en ids
// (checkout, upsell) y no tienen el slug a mano.
export const PREORDER_SLUG_BY_PRODUCT = {
  71: "mic",
};

// Se deriva del mapa de arriba a proposito: dar de alta un libro tiene que ser
// UNA linea, no dos que se pueden desincronizar.
export const PREORDER_LANGS_BY_PRODUCT = Object.fromEntries(
  Object.entries(PREORDER_SLUG_BY_PRODUCT).map(([id, slug]) => [
    id,
    PREORDER_LANGS_BY_SLUG[slug],
  ])
);

// Fecha en la que se habilita la descarga de los idiomas que faltan (ISO, para
// el JSON-LD). Es la misma que muestra el portal a los compradores; si cambia,
// cambia tambien en los siete locales (books.json / checkout.json /
// common.json) y en el respondedor de WhatsApp (admin-back ClaudeService).
//
// El valor puede ser una fecha sola (todos los idiomas que faltan salen juntos)
// o un mapa por idioma con `default`. Musica Ilustrada de Cero
// necesita lo segundo: el español se entrega el 30-nov-2026 y los otros seis el
// 10-dic. Leer siempre con releaseIsoFor(), no indexando a mano.
export const PREORDER_RELEASE_ISO_BY_SLUG = {
  mic: { es: "2026-11-30", default: "2026-12-10" },
};

const DEFAULT_LANG = "es";

function normalizeLang(lang) {
  const l = String(lang || "").toLowerCase().split("-")[0];
  return l || DEFAULT_LANG;
}

/** ¿El libro de ese slug sigue en preventa para ese idioma? */
export function isPreorderSlug(slug, lang) {
  const langs = PREORDER_LANGS_BY_SLUG[slug];
  return Array.isArray(langs) && langs.includes(normalizeLang(lang));
}

/** Igual pero por id de producto base (1, 11, 21, 31, 41, 51, 61). */
export function isPreorderProduct(productId, lang) {
  const langs = PREORDER_LANGS_BY_PRODUCT[Number(productId)];
  return Array.isArray(langs) && langs.includes(normalizeLang(lang));
}

/**
 * Fecha ISO en la que ese idioma puede descargar el libro, o null si el libro
 * ya salio en ese idioma. Acepta las dos formas de
 * PREORDER_RELEASE_ISO_BY_SLUG (string sola o mapa por idioma).
 */
export function releaseIsoFor(slug, lang) {
  const valor = PREORDER_RELEASE_ISO_BY_SLUG[slug];
  if (!valor) return null;
  if (typeof valor === "string") return valor;
  const l = normalizeLang(lang);
  return valor[l] || valor.default || null;
}

/**
 * Para las pantallas que ya traen la metadata del libro en un objeto con
 * `preorderLangs` (BOOK_META de _collection y de home-libro2, PRODUCT_META del
 * checkout). Devuelve false si el objeto no declara idiomas de preventa.
 */
export function isPreorderMeta(meta, lang) {
  const langs = meta?.preorderLangs;
  return Array.isArray(langs) && langs.includes(normalizeLang(lang));
}

// Orden de los idiomas dentro de cada familia de productos: el id 71 es el español y
// 72-78 los otros, en este orden. Es el mismo mapa que productIdForLang del backend.
const LANGS_POR_OFFSET = ["es", "en", "pt", "fr", "it", "de", "ja", "tr"];

/**
 * Fecha de entrega de la EDICION que alguien compro, deducida de su id de producto.
 *
 * NO es lo mismo que releaseIsoForProduct(): esa contesta "que fecha le muestro al que
 * esta navegando en tal idioma". Esta contesta "cuando se entrega este producto", que es
 * lo que hay que decirle a alguien que ya pago: quien reservo la inglesa la recibe el
 * 10-dic aunque despues lea la web en español.
 */
export function releaseIsoForEdition(productId) {
  const pid = Number(productId);
  if (!Number.isFinite(pid) || pid <= 0) return null;
  const base = pid - ((pid - 1) % 10);
  const slug = PREORDER_SLUG_BY_PRODUCT[base];
  if (!slug) return null;
  return releaseIsoFor(slug, LANGS_POR_OFFSET[pid - base] || DEFAULT_LANG);
}

/** Igual que releaseIsoFor() pero por id de producto base (71, no "mic"). */
export function releaseIsoForProduct(productId, lang) {
  const slug = PREORDER_SLUG_BY_PRODUCT[Number(productId)];
  return slug ? releaseIsoFor(slug, lang) : null;
}

/**
 * La fecha de salida escrita en el idioma en el que la va a leer el comprador.
 *
 * El timeZone UTC no es opcional: la fecha se arma con Date.UTC y sin eso el
 * navegador la pasa a hora local, asi que en cualquier huso al oeste imprime el
 * dia ANTERIOR (en Argentina el 10-dic salia "December 9").
 *
 * home-portal tiene su propia copia de esto, escrita antes que esta funcion.
 */
export function formatReleaseDate(iso, localeLang) {
  if (!iso) return "";
  const [a, m, d] = String(iso).split("-").map(Number);
  if (!a || !m || !d) return String(iso);
  try {
    return new Intl.DateTimeFormat(localeLang || DEFAULT_LANG, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(a, m - 1, d)));
  } catch {
    return String(iso);
  }
}
